import axios from 'axios';
import { APP_VERSION } from './buildInfo';

// Get base URL from environment
const getBaseUrl = (): string => {
  if (import.meta.env.MODE === 'production' || import.meta.env.PROD) {
    return ''; // Relative path for production
  }
  return 'http://localhost:7777'; // Absolute path for development
};

export interface VersionInfo {
  backendVersion: string;
  schedulingVersion?: string;
  buildCommit?: string;
  buildTime?: string;
}

const FRONTEND_VERSION = APP_VERSION;

/**
 * Check if frontend and backend versions are compatible
 * @returns Promise with version info and compatibility status
 */
export async function checkVersionCompatibility(): Promise<{
  frontendVersion: string;
  backendVersion: string | null;
  compatible: boolean;
  error?: string;
}> {
  try {
    const response = await axios.get<VersionInfo>(`${getBaseUrl()}/api/version`, {
      timeout: 5000,
      validateStatus: (status) => status < 500, // Don't throw on 4xx
    });

    if (response.status === 200 && response.data && response.data.backendVersion) {
      const backendVersion = response.data.backendVersion;
      // For now, we consider versions compatible if major and minor match
      // This allows patch updates without breaking compatibility
      const frontendMajorMinor = FRONTEND_VERSION.split('.').slice(0, 2).join('.');
      const backendMajorMinor = backendVersion.split('.').slice(0, 2).join('.');
      const compatible = frontendMajorMinor === backendMajorMinor;

      return {
        frontendVersion: FRONTEND_VERSION,
        backendVersion,
        compatible,
        ...(compatible ? {} : { error: `Version mismatch: Frontend ${FRONTEND_VERSION} vs Backend ${backendVersion}` }),
      };
    }

    return {
      frontendVersion: FRONTEND_VERSION,
      backendVersion: null,
      compatible: false,
      error: 'Could not retrieve backend version',
    };
  } catch (error: any) {
    console.error('Version check failed:', error);
    return {
      frontendVersion: FRONTEND_VERSION,
      backendVersion: null,
      compatible: false,
      error: error.message || 'Failed to check version',
    };
  }
}

/**
 * Get the current frontend version
 */
export function getFrontendVersion(): string {
  return FRONTEND_VERSION;
}

